import { transformEstablishment } from '@/common/integrations/menhir/employee/establishments/establishments.dto';
import { transformInsuranceCoveredMember } from '@/common/integrations/menhir/employee/insurance/insurance.dto';
import { transformMovement } from '@/common/integrations/menhir/employee/movements/movements.dto';
import { transformOrderOperation } from '@/common/integrations/menhir/employee/orders/orders.dto';
import { isOrderWithPolicy } from '@/common/integrations/menhir/employee/orders/orders.types';
import {
  IMenhirOrder,
  TOrderCategorySlug,
} from '@/common/integrations/menhir/employee/orders/orders.types';
import {
  isDonationOrder,
  isInsuranceOrder,
} from '@/common/integrations/menhir/employee/orders/orders.types';
import { IOrder } from '@/common/integrations/menhir/employee/orders/orders.types';
import { IOrderWithPolicy } from '@/common/integrations/menhir/employee/orders/orders.types';
import { IDonationOrder } from '@/common/integrations/menhir/employee/orders/orders.types';
import {
  ICatalogCategory,
  ICatalogPolicy,
  ICatalogProduct,
  ICatalogProductRules,
  ICatalogProductWithProvider,
  ICatalogProvider,
  IInsuranceOrder,
} from '@/common/types/catalog';
import {
  IMenhirCatalogCategory,
  IMenhirCatalogPolicy,
  IMenhirCatalogProduct,
  IMenhirCatalogProductRules,
  IMenhirCatalogProductWithProvider,
  IMenhirCatalogProvider,
} from '@/common/types/menhir/catalog';

const isProductWithProvider = (
  product: IMenhirCatalogProduct<TOrderCategorySlug>
): product is IMenhirCatalogProductWithProvider<TOrderCategorySlug> =>
  'provider' in product && !!product.provider;

const transformCategory = <T extends TOrderCategorySlug>(
  category: IMenhirCatalogCategory<T>
): ICatalogCategory<T> => ({
  id: category.id,
  name: category.name,
  description: category.description,
  slug: category.slug,
});

const transformProductRules = (
  rules?: IMenhirCatalogProductRules
): ICatalogProductRules => ({
  minAmountWithoutRecurrence: rules?.min_amount_without_recurrence,
  minAmountWithRecurrence: rules?.min_amount_with_recurrence,
  minimumTopup: rules?.minimum_topup,
  minimumTopupWithRecurrence: rules?.minimum_topup_with_recurrence,
  hidePolicyNumber: rules?.hide_policy_number
    ? rules.hide_policy_number
    : false,
  receiptTaxNumberType: rules?.receipt_tax_number_type,
  maxChildAge: rules?.max_child_age,
  riskIndicator: rules?.risk_indicator,
  requiresAgreement: rules?.requires_agreement,
  setupEnable: rules?.setup_enable,
  minPurchaseAmount: rules?.min_purchase_amount,
});

const transformProductProvider = <T extends TOrderCategorySlug>(
  provider: IMenhirCatalogProvider<T>
): ICatalogProvider<T> => ({
  id: provider.id,
  enabled: provider.enabled,
  name: provider.name,
  slug: provider.slug,
  logoUrl: provider.logo_url,
});

export const transformProduct = <T extends TOrderCategorySlug>(
  product: IMenhirCatalogProduct<T>
): ICatalogProduct<T> => {
  const baseProduct: ICatalogProduct<T> = {
    id: product.id,
    files: product.files,
    slug: product.slug,
    category: transformCategory(product.category),
    rules: transformProductRules(product.rules),
    status: product.status,
    discontinuationDate: product.discontinuation_date,
    type: product.type,
    metadata: product.metadata
      ? {
          externalUrl: product.metadata.external_url,
        }
      : undefined,
  };
  if (isProductWithProvider(product)) {
    return {
      ...baseProduct,
      provider: transformProductProvider(product.provider),
    } as ICatalogProductWithProvider<T>;
  }
  return baseProduct;
};

export const transformPolicy = (
  policy: IMenhirCatalogPolicy
): ICatalogPolicy => ({
  id: policy.id,
  number: policy.policy_number,
  status: policy.status,
  files: policy.files ?? [],
  movements:
    policy.movements?.map((movement) => transformMovement(movement)) ?? [],
});

export const transformBaseOrder = <T extends TOrderCategorySlug>(
  order: IMenhirOrder<T>
): IOrder<T> => ({
  id: order.id,
  isClosed: order.is_closed,
  product: transformProduct(order.product),
  status: order.status,
  files: order.files ?? [],
  amount: order.amount,
  reference: order.reference,
  operation: order.operation
    ? transformOrderOperation(order.operation)
    : undefined,
  recurrence: order.recurrence,
  insertedAt: order.inserted_at,
});

export const transformMovementOrder = <T extends TOrderCategorySlug>(
  order: Omit<IMenhirOrder<T>, 'movements'>
): Omit<IOrder, 'movements' | 'files'> => {
  const baseOrder: Omit<IOrder<T>, 'movements' | 'files'> = {
    id: order.id,
    isClosed: order.is_closed,
    product: transformProduct(order.product),
    status: order.status,
    amount: order.amount,
    reference: order.reference,
    operation: order.operation
      ? transformOrderOperation(order.operation)
      : undefined,
  };
  if (isInsuranceOrder(order)) {
    return {
      ...baseOrder,
      coveredPerson: transformInsuranceCoveredMember(order.covered_person),
    } as Omit<IInsuranceOrder, 'files' | 'movements'>;
  }
  if (isOrderWithPolicy(order)) {
    return {
      ...baseOrder,
      policy: order.policy ? transformPolicy(order.policy) : null,
    } as IOrderWithPolicy<T>;
  }
  if (isDonationOrder(order)) {
    return {
      ...baseOrder,
      establishment: transformEstablishment(order.establishment),
    } as Omit<IDonationOrder, 'files' | 'movements'>;
  }

  return baseOrder;
};

export const CatalogDTO = {
  transformMovementOrder,
  transformPolicy,
  transformProduct,
  transformProductRules,
  transformProductProvider,
  transformBaseOrder,
};
